/**
 * netClient — WebSocket 客户端单例（PROTOCOL.md v3）。
 *
 * 职责：
 *  1. 连接生命周期：建立 / HELLO 握手 / 指数退避自动重连；
 *  2. 身份持久化：playerId + resumeToken + nickname（localStorage），
 *     重连时凭以恢复队伍席位与对局（HELLO_ACK.resumed）；
 *  3. 心跳与时钟：30s PING，PONG 计算 RTT 与 serverNow−clientNow 偏移
 *     （偏移用于 turnDeadlineAt 等服务端时间戳的本地渲染）；
 *  4. 消息路由：会话类消息写入 netStore，全部消息分发给场景订阅者。
 *
 * 本模块不做页面导航——导航由场景订阅 $net 后自行决定。
 */
import {
  HEARTBEAT_DEAD_MS,
  HEARTBEAT_PING_MS,
  PROTOCOL_VERSION,
  type OnlinePlayerCount,
} from '@shared/net/constants';
import { asPayload, decodeMessage, encodeMessage } from '@shared/net/codec';
import { sanitizeNickname } from '@shared/net/identity';
import type {
  C2SPayloadMap,
  C2SType,
  GameOverPayload,
  GameStartPayload,
  HelloAckPayload,
  MatchFoundPayload,
  PartyLeftPayload,
  PartyUpdatePayload,
  PongPayload,
  QueueCancelledPayload,
  QueuedPayload,
  QueueStatusPayload,
  RematchUpdatePayload,
  RoomClosedPayload,
  S2CPayloadMap,
  S2CType,
  StateSyncPayload,
  ErrorPayload,
  WSMessage,
} from '@shared/net/messages';
import { APP_VERSION, netLogger } from '../debug/logger';
import { toLogErr } from '@shared/debug/logger';
import { updateSettings } from '../store/settingsStore';
import { $net, clearNetMatch, patchNet, setNetStatus } from '../store/netStore';

// ── Identity persistence ─────────────────────────────────

const IDENTITY_KEY = 'mc.net.v1';

interface StoredIdentity {
  playerId: string | null;
  resumeToken: string | null;
  nickname: string;
}

function loadIdentity(): StoredIdentity {
  try {
    const raw = localStorage.getItem(IDENTITY_KEY);
    if (!raw) return { playerId: null, resumeToken: null, nickname: '' };
    const p = JSON.parse(raw) as Partial<StoredIdentity>;
    return {
      playerId: typeof p.playerId === 'string' ? p.playerId : null,
      resumeToken: typeof p.resumeToken === 'string' ? p.resumeToken : null,
      nickname: typeof p.nickname === 'string' ? p.nickname : '',
    };
  } catch {
    return { playerId: null, resumeToken: null, nickname: '' };
  }
}

function saveIdentity(id: StoredIdentity): void {
  try {
    localStorage.setItem(IDENTITY_KEY, JSON.stringify(id));
  } catch {
    /* private mode — session-only identity */
  }
}

// ── URL ──────────────────────────────────────────────────

/** Dev 走 vite 代理（/ws → :3001），生产同源反代。 */
function wsUrl(): string {
  const proto = location.protocol === 'https:' ? 'wss:' : 'ws:';
  return `${proto}//${location.host}/ws`;
}

// ── Client ───────────────────────────────────────────────

type Handler = (message: WSMessage) => void;

const RECONNECT_BASE_MS = 1_000;
const RECONNECT_CAP_MS = 15_000;
/** HELLO_ACK.resumed 后的恢复消息（PARTY_UPDATE/STATE_SYNC…）等待上限。 */
const RESUME_SETTLE_MS = 3_000;

class NetClient {
  private ws: WebSocket | null = null;
  private seq = 0;
  private handlers = new Map<string, Set<Handler>>();
  private heartbeat: ReturnType<typeof setInterval> | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private resumeSettleTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempts = 0;
  private intentionalClose = false;
  private everConnected = false;
  private lastMessageAt = 0;

  /** 建立连接（幂等）。面板打开 / 页签恢复时调用。 */
  connect(): void {
    if (
      this.ws &&
      (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)
    ) {
      return;
    }
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.intentionalClose = false;
    const st = $net.get().status;
    if (st === 'offline' || st === 'error' || st === 'idle')
      setNetStatus('connecting', { error: null });
    try {
      this.ws = new WebSocket(wsUrl());
    } catch (err) {
      netLogger.warn('WebSocket 创建失败', { event: 'net.ws.create_failed', err: toLogErr(err) });
      setNetStatus('error', { error: 'INTERNAL' });
      this.scheduleReconnect();
      return;
    }
    this.ws.onopen = () => this.onOpen();
    this.ws.onmessage = (ev) => this.onMessage(ev);
    this.ws.onclose = () => this.onClose();
    this.ws.onerror = () => {
      /* onclose follows — reconnect logic lives there */
    };
  }

  /** 主动断开（不自动重连）。一般仅用于测试/调试。 */
  disconnect(): void {
    this.intentionalClose = true;
    this.stopHeartbeat();
    if (this.reconnectTimer !== null) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.ws?.close();
    this.ws = null;
    setNetStatus('offline');
  }

  // ── Send / subscribe ───────────────────────────────────

  /** 发送一条 C2S 消息；socket 未就绪返回 false（调用方多为用户驱动）。 */
  send<K extends C2SType>(type: K, payload: C2SPayloadMap[K]): boolean {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      netLogger.warn('socket 未就绪，消息被丢弃', { event: 'net.send.dropped', type });
      return false;
    }
    this.ws.send(encodeMessage(type, payload, this.seq++));
    return true;
  }

  /** 订阅一类 S2C 消息（场景级）；返回退订函数。 */
  on<K extends S2CType>(
    type: K,
    handler: (payload: S2CPayloadMap[K], message: WSMessage) => void
  ): () => void {
    const set = this.handlers.get(type) ?? new Set<Handler>();
    const wrapped: Handler = (m) => handler(asPayload<S2CPayloadMap[K]>(m), m);
    set.add(wrapped);
    this.handlers.set(type, set);
    return () => {
      set.delete(wrapped);
    };
  }

  // ── Intents（C2S 语义封装） ────────────────────────────

  /** 更新昵称：持久化；若已连接则重连以在 HELLO 中生效。 */
  setNickname(raw: string): void {
    const id = loadIdentity();
    const nick = sanitizeNickname(raw) ?? '';
    if (nick === id.nickname) return;
    saveIdentity({ ...id, nickname: nick });
    patchNet({ nickname: nick });
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      netLogger.info('昵称变更，重连以生效', { event: 'net.identity.changed' });
      this.ws.close(); // onClose 走自动重连
    }
  }

  createParty(): void {
    this.send('PARTY_CREATE', {});
  }

  joinParty(code: string): void {
    this.send('PARTY_JOIN', { code: code.trim().toUpperCase() });
  }

  leaveParty(): void {
    this.send('PARTY_LEAVE', {});
  }

  queueJoin(playerCount: OnlinePlayerCount): void {
    this.send('QUEUE_JOIN', { playerCount });
  }

  queueCancel(): void {
    this.send('QUEUE_CANCEL', {});
  }

  sendMove(from: { q: number; r: number }, to: { q: number; r: number }): boolean {
    return this.send('MOVE', { from, to });
  }

  /** 离开当前对局/房间（对局中=认输，终局=退出房间）。 */
  leaveGame(): void {
    this.send('LEAVE_GAME', {});
    clearNetMatch();
    setNetStatus('idle');
  }

  voteRematch(): void {
    this.send('REMATCH', {});
  }

  // ── Socket lifecycle ───────────────────────────────────

  private onOpen(): void {
    this.reconnectAttempts = 0;
    this.lastMessageAt = Date.now();
    const id = loadIdentity();
    this.send('HELLO', {
      protocolVersion: PROTOCOL_VERSION,
      clientVersion: APP_VERSION,
      nickname: id.nickname,
      // exactOptionalPropertyTypes：缺省字段须缺席而非显式 undefined。
      ...(id.playerId ? { playerId: id.playerId } : {}),
      ...(id.resumeToken ? { resumeToken: id.resumeToken } : {}),
    });
    this.startHeartbeat();
  }

  private onClose(): void {
    this.stopHeartbeat();
    this.ws = null;
    if (this.intentionalClose) return;
    // 保留 party/match 现场：重连后由服务端恢复消息校准。
    setNetStatus('connecting');
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.reconnectTimer !== null) return;
    const attempt = this.reconnectAttempts++;
    const delay = Math.min(RECONNECT_BASE_MS * 2 ** attempt, RECONNECT_CAP_MS);
    if (!this.everConnected && attempt >= 2) {
      setNetStatus('error', { error: 'INTERNAL' });
    }
    netLogger.info('计划重连', { event: 'net.ws.reconnect_scheduled', attempt, delay });
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      if (Date.now() - this.lastMessageAt > HEARTBEAT_DEAD_MS) {
        netLogger.warn('心跳超时，关闭并重连', { event: 'net.ws.heartbeat_dead' });
        this.ws.close();
        return;
      }
      this.send('PING', { t: Date.now() });
    }, HEARTBEAT_PING_MS);
  }

  private stopHeartbeat(): void {
    if (this.heartbeat !== null) {
      clearInterval(this.heartbeat);
      this.heartbeat = null;
    }
  }

  // ── Message routing ────────────────────────────────────

  private onMessage(ev: MessageEvent): void {
    if (typeof ev.data !== 'string') return;
    this.lastMessageAt = Date.now();
    const decoded = decodeMessage(ev.data);
    if (!decoded.ok) {
      netLogger.warn('无法解码的帧', { event: 'net.decode.failed', error: decoded.error });
      return;
    }
    const msg = decoded.message;
    this.applyToStore(msg);
    const set = this.handlers.get(msg.type);
    if (set) for (const h of [...set]) h(msg);
  }

  /** 会话类消息 → netStore（场景无关的状态同步）。 */
  private applyToStore(msg: WSMessage): void {
    switch (msg.type) {
      case 'HELLO_ACK':
        this.onHelloAck(asPayload<HelloAckPayload>(msg));
        break;
      case 'PARTY_UPDATE': {
        const p = asPayload<PartyUpdatePayload>(msg);
        patchNet({ party: { code: p.code, leaderId: p.leaderId, members: p.members } });
        if ($net.get().status !== 'in-queue') setNetStatus('party');
        break;
      }
      case 'PARTY_LEFT': {
        const p = asPayload<PartyLeftPayload>(msg);
        patchNet({ party: null });
        // 'matched' 由随后的 MATCH_FOUND 接管；其余回到 idle。
        if (p.reason !== 'matched' && $net.get().status !== 'in-match') setNetStatus('idle');
        break;
      }
      case 'QUEUED': {
        const p = asPayload<QueuedPayload>(msg);
        patchNet({ queuedAt: p.queuedAt, playerCount: p.playerCount, queueStatus: null });
        setNetStatus('in-queue');
        break;
      }
      case 'QUEUE_STATUS': {
        // v3.1（§6.4）：等待面板锚点帧；UI 以 receivedAt 自减倒计时。
        const p = asPayload<QueueStatusPayload>(msg);
        patchNet({ queueStatus: { ...p, receivedAt: Date.now() } });
        break;
      }
      case 'QUEUE_CANCELLED': {
        asPayload<QueueCancelledPayload>(msg); // reason 仅供日志
        patchNet({ queuedAt: null, queueStatus: null });
        setNetStatus($net.get().party ? 'party' : 'idle');
        break;
      }
      case 'MATCH_FOUND':
        this.onMatchFound(asPayload<MatchFoundPayload>(msg));
        break;
      case 'GAME_START':
        this.onGameStart(asPayload<GameStartPayload>(msg));
        break;
      case 'STATE_SYNC':
        this.onStateSync(asPayload<StateSyncPayload>(msg));
        break;
      case 'GAME_OVER': {
        const p = asPayload<GameOverPayload>(msg);
        patchNet({ gameOver: p, rematch: null, roomClosed: null });
        break;
      }
      case 'REMATCH_UPDATE':
        patchNet({ rematch: asPayload<RematchUpdatePayload>(msg) });
        break;
      case 'ROOM_CLOSED': {
        const p = asPayload<RoomClosedPayload>(msg);
        clearNetMatch(); // 会抹掉 roomClosed，之后重新写入供 UI 读取
        patchNet({ roomClosed: p });
        setNetStatus('idle');
        break;
      }
      case 'PONG': {
        const p = asPayload<PongPayload>(msg);
        const rtt = Math.max(0, Date.now() - p.t);
        // 信封 timestamp 为服务端发送时刻；+RTT/2 估计本地此刻的服务端时钟。
        patchNet({ rttMs: rtt, clockOffset: msg.timestamp + rtt / 2 - Date.now() });
        break;
      }
      case 'ERROR':
        this.onError(asPayload<ErrorPayload>(msg));
        break;
      default:
        break; // STATE_UPDATE / PLAYER_PRESENCE / MOVE_REJECTED → 场景订阅
    }
  }

  private onHelloAck(p: HelloAckPayload): void {
    this.everConnected = true;
    saveIdentity({ playerId: p.playerId, resumeToken: p.resumeToken, nickname: p.nickname });
    patchNet({ playerId: p.playerId, nickname: p.nickname, error: null });
    netLogger.info('HELLO_ACK', {
      event: 'net.hello.ack',
      playerId: p.playerId,
      resumed: p.resumed,
      serverVersion: p.serverVersion,
    });
    if (!p.resumed) {
      // 服务端视角的全新会话：清掉本地可能残留的 party/match 现场。
      patchNet({ party: null, queuedAt: null });
      clearNetMatch();
      setNetStatus('idle');
      return;
    }
    // 恢复消息（PARTY_UPDATE / QUEUED / STATE_SYNC）随后到达；
    // 超时未至则保守回落 idle（协议外情形，仅防御）。
    if (this.resumeSettleTimer !== null) clearTimeout(this.resumeSettleTimer);
    this.resumeSettleTimer = setTimeout(() => {
      this.resumeSettleTimer = null;
      if ($net.get().status === 'connecting') setNetStatus('idle');
    }, RESUME_SETTLE_MS);
  }

  private onMatchFound(p: MatchFoundPayload): void {
    patchNet({
      match: {
        matchId: p.matchId,
        playerCount: p.playerCount,
        yourSeat: p.yourSeat,
        players: p.players,
        startsAt: Date.now() + p.startsInMs,
      },
      queuedAt: null,
      queueStatus: null,
      party: null,
      gameStart: null,
      gameOver: null,
      rematch: null,
      roomClosed: null,
    });
    setNetStatus('matched');
    // GameScene 在构造时冻结 $settings —— 导航之前把模式参数写入。
    updateSettings({ mode: 'online', playerCount: p.playerCount });
    netLogger.info('匹配成功', {
      event: 'match.found',
      matchId: p.matchId,
      playerCount: p.playerCount,
      yourSeat: p.yourSeat,
    });
  }

  private onGameStart(p: GameStartPayload): void {
    patchNet({ gameStart: p, gameOver: null, rematch: null, roomClosed: null });
    setNetStatus('in-match');
    netLogger.info('对局开始', { event: 'match.game_start', matchId: p.matchId });
  }

  /** 断线恢复：以对局快照重建本地会话。 */
  private onStateSync(p: StateSyncPayload): void {
    patchNet({
      match: {
        matchId: p.matchId,
        playerCount: p.players.length as OnlinePlayerCount,
        yourSeat: p.yourSeat,
        players: p.players,
        startsAt: 0,
      },
      gameStart: { matchId: p.matchId, state: p.state, turnDeadlineAt: p.turnDeadlineAt },
      gameOver: null,
      rematch: p.rematch ?? null,
      roomClosed: null,
    });
    setNetStatus('in-match');
    netLogger.info('对局状态已同步', {
      event: 'match.state_sync',
      matchId: p.matchId,
      phase: p.phase,
    });
  }

  private onError(p: ErrorPayload): void {
    netLogger.warn('服务端错误', {
      event: 'net.error',
      code: p.code,
      message: p.message,
      fatal: p.fatal,
    });
    patchNet({ error: p.code });
    if (p.fatal) {
      // 协议/身份级错误：本地身份不再可信，清除后重连可获全新身份。
      if (p.code === 'AUTH_FAILED' || p.code === 'SESSION_REPLACED') {
        const id = loadIdentity();
        saveIdentity({ ...id, playerId: null, resumeToken: null });
        patchNet({ party: null, queuedAt: null });
        clearNetMatch();
      }
      this.ws?.close(); // onClose 自动重连（SESSION_REPLACED 除外也无妨）
    }
  }
}

/** 全局单例。 */
export const netClient = new NetClient();
